import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Radar, LayoutDashboard, Building2, Megaphone, MapPin, Compass,
  Image, Inbox, ScrollText, Settings, LogOut, Menu, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/clients", label: "Clients", icon: Building2 },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/geofences", label: "Geofences", icon: MapPin },
  { to: "/admin/geofence-builder", label: "Geofence Builder", icon: Compass },
  { to: "/admin/creatives", label: "Creative Assets", icon: Image },
  { to: "/admin/contacts", label: "Enquiries", icon: Inbox },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/settings", label: "System Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const sidebar = (suffix = "") => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-[72px] flex items-center gap-2.5 border-b border-white/5">
        <span className="flex items-center justify-center w-9 h-9 rounded-md bg-blue-600/20 border border-blue-500/30">
          <Radar className="w-5 h-5 text-cyan-400" />
        </span>
        <div>
          <span className="font-display font-bold text-white text-sm">GEOTRAQ <span className="text-cyan-400">AI</span></span>
          <p className="text-[9px] tracking-[0.25em] text-slate-500 uppercase">Admin Console</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1" data-testid={`admin-nav${suffix}`}>
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}${suffix}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600/15 text-cyan-300 border border-blue-500/25" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/5">
        <button onClick={doLogout} data-testid={`admin-logout${suffix}`}
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/5 w-full transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:block w-60 shrink-0 border-r border-white/5 bg-[#060b16] fixed inset-y-0 z-40">{sidebar("")}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-[#060b16] border-r border-white/5">{sidebar("-mobile")}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="h-[72px] glass border-x-0 border-t-0 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white p-1.5" onClick={() => setMobileOpen(!mobileOpen)} data-testid="admin-mobile-menu" aria-label="Menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div>
              <p className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Administrator</p>
              <h1 className="font-display font-bold text-white leading-tight" data-testid="admin-name">{user?.profile?.name || user?.profile?.email}</h1>
            </div>
          </div>
          <span className="text-[10px] font-bold tracking-widest px-2.5 py-1.5 rounded bg-blue-600/15 text-blue-300 border border-blue-500/25" data-testid="admin-role-badge">
            {user?.profile?.role || "ADMIN"}
          </span>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
